import { useState, useEffect, useCallback } from 'react';
import AdminLayout from '@/pages/admin/components/AdminLayout';
import { supabase } from '@/lib/supabase';

interface Feedback {
  id: number;
  user_id: string | null;
  phone: string | null;
  feedback_type: string;
  content: string;
  contact: string | null;
  status: string;
  reply: string | null;
  replied_at: string | null;
  created_at: string;
}

const STATUS_MAP: Record<string, { label: string; color: string }> = {
  pending: { label: '待处理', color: 'bg-amber-100 text-amber-700' },
  processing: { label: '处理中', color: 'bg-blue-100 text-blue-700' },
  resolved: { label: '已解决', color: 'bg-emerald-100 text-emerald-700' },
  closed: { label: '已关闭', color: 'bg-gray-100 text-gray-500' },
};

const TYPE_COLORS: Record<string, string> = {
  '产品建议': 'bg-violet-100 text-violet-700',
  '功能问题': 'bg-rose-100 text-rose-700',
  '兑换问题': 'bg-orange-100 text-orange-700',
  '物流问题': 'bg-sky-100 text-sky-700',
  '其他': 'bg-gray-100 text-gray-600',
};

const AdminFeedbacksPage = () => {
  const [feedbacks, setFeedbacks] = useState<Feedback[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Feedback | null>(null);
  const [replyText, setReplyText] = useState('');
  const [replyStatus, setReplyStatus] = useState('resolved');
  const [saving, setSaving] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 20;

  const fetchFeedbacks = useCallback(async () => {
    setLoading(true);
    let query = supabase
      .from('feedbacks')
      .select('*', { count: 'exact' })
      .order('created_at', { ascending: false })
      .range((page - 1) * PAGE_SIZE, page * PAGE_SIZE - 1);

    if (filterStatus !== 'all') query = query.eq('status', filterStatus);
    if (filterType !== 'all') query = query.eq('feedback_type', filterType);
    if (search.trim()) {
      query = query.or(`content.ilike.%${search.trim()}%,phone.ilike.%${search.trim()}%,contact.ilike.%${search.trim()}%`);
    }

    const { data, count, error } = await query;
    if (!error) {
      setFeedbacks(data ?? []);
      setTotal(count ?? 0);
    }
    setLoading(false);
  }, [filterStatus, filterType, search, page]);

  useEffect(() => {
    fetchFeedbacks();
  }, [fetchFeedbacks]);

  const handleSaveReply = async () => {
    if (!selected) return;
    setSaving(true);
    const { error } = await supabase
      .from('feedbacks')
      .update({
        status: replyStatus,
        reply: replyText.trim() || null,
        replied_at: new Date().toISOString(),
      })
      .eq('id', selected.id);
    setSaving(false);
    if (!error) {
      setSelected(null);
      setReplyText('');
      fetchFeedbacks();
    }
  };

  const openDetail = (fb: Feedback) => {
    setSelected(fb);
    setReplyText(fb.reply ?? '');
    setReplyStatus(fb.status === 'pending' ? 'resolved' : fb.status);
  };

  const totalPages = Math.ceil(total / PAGE_SIZE);

  const pendingCount = feedbacks.filter(f => f.status === 'pending').length;

  return (
    <AdminLayout title="用户反馈">
      <div className="space-y-4">
        {/* Stats bar */}
        <div className="grid grid-cols-4 gap-4">
          {[
            { label: '全部反馈', value: total, icon: 'ri-chat-3-line', color: 'text-gray-700', bg: 'bg-gray-100' },
            { label: '待处理', value: feedbacks.filter(f => f.status === 'pending').length, icon: 'ri-time-line', color: 'text-amber-600', bg: 'bg-amber-50' },
            { label: '处理中', value: feedbacks.filter(f => f.status === 'processing').length, icon: 'ri-loader-4-line', color: 'text-blue-600', bg: 'bg-blue-50' },
            { label: '已解决', value: feedbacks.filter(f => f.status === 'resolved').length, icon: 'ri-check-double-line', color: 'text-emerald-600', bg: 'bg-emerald-50' },
          ].map((s) => (
            <div key={s.label} className="bg-white rounded-xl p-4 flex items-center gap-3">
              <div className={`w-10 h-10 flex items-center justify-center rounded-lg ${s.bg}`}>
                <i className={`${s.icon} text-lg ${s.color}`} />
              </div>
              <div>
                <p className="text-xs text-gray-400">{s.label}</p>
                <p className={`text-xl font-bold ${s.color}`}>{s.value}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl p-4 flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
            <input
              type="text"
              placeholder="搜索内容、手机号、联系方式..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-lg text-sm outline-none focus:border-rose-300"
            />
          </div>
          <select
            value={filterStatus}
            onChange={(e) => { setFilterStatus(e.target.value); setPage(1); }}
            className="border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none cursor-pointer"
          >
            <option value="all">全部状态</option>
            <option value="pending">待处理</option>
            <option value="processing">处理中</option>
            <option value="resolved">已解决</option>
            <option value="closed">已关闭</option>
          </select>
          <select
            value={filterType}
            onChange={(e) => { setFilterType(e.target.value); setPage(1); }}
            className="border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none cursor-pointer"
          >
            <option value="all">全部类型</option>
            {['产品建议', '功能问题', '兑换问题', '物流问题', '其他'].map(t => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
          <button
            onClick={() => fetchFeedbacks()}
            className="flex items-center gap-1.5 px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-refresh-line" /> 刷新
          </button>
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50">
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 w-12">#</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">用户</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">类型</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 w-80">内容</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">联系方式</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">状态</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">提交时间</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">操作</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={8} className="text-center py-16 text-gray-400">
                    <i className="ri-loader-4-line text-2xl animate-spin block mb-2" />
                    加载中...
                  </td>
                </tr>
              ) : feedbacks.length === 0 ? (
                <tr>
                  <td colSpan={8} className="text-center py-16 text-gray-400">
                    <i className="ri-chat-off-line text-3xl block mb-2" />
                    暂无反馈数据
                  </td>
                </tr>
              ) : feedbacks.map((fb, idx) => (
                <tr key={fb.id} className="border-b border-gray-50 hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3 text-gray-400 text-xs">{(page - 1) * PAGE_SIZE + idx + 1}</td>
                  <td className="px-4 py-3">
                    <span className="text-gray-700 font-medium">{fb.phone ?? '匿名'}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${TYPE_COLORS[fb.feedback_type] ?? 'bg-gray-100 text-gray-600'}`}>
                      {fb.feedback_type}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <p className="text-gray-700 line-clamp-2 leading-relaxed">{fb.content}</p>
                  </td>
                  <td className="px-4 py-3 text-gray-500 text-xs">{fb.contact ?? '—'}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_MAP[fb.status]?.color ?? 'bg-gray-100 text-gray-500'}`}>
                      {STATUS_MAP[fb.status]?.label ?? fb.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-gray-400 text-xs whitespace-nowrap">
                    {new Date(fb.created_at).toLocaleString('zh-CN', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })}
                  </td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => openDetail(fb)}
                      className="flex items-center gap-1 px-3 py-1.5 bg-rose-50 text-rose-600 rounded-lg text-xs font-medium hover:bg-rose-100 cursor-pointer whitespace-nowrap transition-colors"
                    >
                      <i className="ri-eye-line" /> 查看
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100">
              <p className="text-xs text-gray-400">共 {total} 条</p>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 text-gray-500 disabled:opacity-40 cursor-pointer hover:bg-gray-50"
                >
                  <i className="ri-arrow-left-s-line" />
                </button>
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  const p = Math.max(1, Math.min(page - 2, totalPages - 4)) + i;
                  return (
                    <button
                      key={p}
                      onClick={() => setPage(p)}
                      className={`w-8 h-8 flex items-center justify-center rounded-lg text-xs cursor-pointer transition-colors ${
                        p === page ? 'bg-rose-500 text-white' : 'border border-gray-200 text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      {p}
                    </button>
                  );
                })}
                <button
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 text-gray-500 disabled:opacity-40 cursor-pointer hover:bg-gray-50"
                >
                  <i className="ri-arrow-right-s-line" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Detail Modal */}
      {selected && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-6" onClick={() => setSelected(null)}>
          <div className="bg-white rounded-2xl w-full max-w-lg overflow-hidden" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <div className="flex items-center gap-2">
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${TYPE_COLORS[selected.feedback_type] ?? 'bg-gray-100 text-gray-600'}`}>
                  {selected.feedback_type}
                </span>
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_MAP[selected.status]?.color}`}>
                  {STATUS_MAP[selected.status]?.label}
                </span>
              </div>
              <button onClick={() => setSelected(null)} className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer">
                <i className="ri-close-line text-lg" />
              </button>
            </div>

            <div className="px-6 py-4 space-y-4 max-h-[70vh] overflow-y-auto">
              {/* User info */}
              <div className="flex items-center gap-4 text-sm">
                <div className="flex items-center gap-1.5 text-gray-500">
                  <i className="ri-user-line text-gray-400" />
                  <span>{selected.phone ?? '匿名用户'}</span>
                </div>
                {selected.contact && (
                  <div className="flex items-center gap-1.5 text-gray-500">
                    <i className="ri-phone-line text-gray-400" />
                    <span>{selected.contact}</span>
                  </div>
                )}
                <div className="flex items-center gap-1.5 text-gray-400 text-xs ml-auto">
                  <i className="ri-time-line" />
                  <span>{new Date(selected.created_at).toLocaleString('zh-CN')}</span>
                </div>
              </div>

              {/* Content */}
              <div className="bg-gray-50 rounded-xl p-4">
                <p className="text-xs text-gray-400 mb-2 font-medium">反馈内容</p>
                <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">{selected.content}</p>
              </div>

              {/* Previous reply */}
              {selected.reply && (
                <div className="bg-emerald-50 rounded-xl p-4">
                  <p className="text-xs text-emerald-600 mb-2 font-medium">已回复</p>
                  <p className="text-sm text-emerald-700 leading-relaxed whitespace-pre-wrap">{selected.reply}</p>
                  {selected.replied_at && (
                    <p className="text-xs text-emerald-400 mt-2">{new Date(selected.replied_at).toLocaleString('zh-CN')}</p>
                  )}
                </div>
              )}

              {/* Reply area */}
              <div>
                <p className="text-xs text-gray-500 font-medium mb-2">回复 / 备注（选填）</p>
                <textarea
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  placeholder="输入回复内容或处理备注..."
                  rows={4}
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-rose-300 resize-none"
                />
              </div>

              {/* Status */}
              <div>
                <p className="text-xs text-gray-500 font-medium mb-2">更新状态</p>
                <div className="flex gap-2 flex-wrap">
                  {Object.entries(STATUS_MAP).map(([key, val]) => (
                    <button
                      key={key}
                      onClick={() => setReplyStatus(key)}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium border cursor-pointer whitespace-nowrap transition-all ${
                        replyStatus === key ? 'border-rose-400 bg-rose-50 text-rose-600' : 'border-gray-200 text-gray-500 hover:border-gray-300'
                      }`}
                    >
                      {val.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-gray-100 flex gap-3">
              <button
                onClick={() => setSelected(null)}
                className="flex-1 py-2.5 border border-gray-200 rounded-xl text-sm text-gray-600 hover:bg-gray-50 cursor-pointer whitespace-nowrap"
              >
                取消
              </button>
              <button
                onClick={handleSaveReply}
                disabled={saving}
                className="flex-1 py-2.5 bg-rose-500 text-white rounded-xl text-sm font-medium hover:bg-rose-600 cursor-pointer whitespace-nowrap disabled:opacity-60 transition-colors"
              >
                {saving ? '保存中...' : '保存处理'}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminFeedbacksPage;
